import React, { useState, useMemo, useEffect } from 'react';
import { ipfsGateway, resolveIpfsUrl, itemTemplates, SUPPORTED_COLLECTIONS } from '../constants/gameConfig';
import { getAssetEcosystemStats, EMBLEM_TEMPLATES } from '../constants/ecosystemAssets';
import placeholderImage from '../assets/equip.png';
import BadgeClaim from '../components/BadgeClaim';

// Collection metadata and AtomicHub Market URL mappings
const COLLECTION_DETAILS = [
  {
    name: 'WHATIF Rewards',
    collectionName: 'whatifreward',
    baseMP: '5 - 13,040 MP',
    role: 'Primary Ecosystem (Firemen, Beasts, Deeds, Gear)',
    marketUrl: 'https://wax.atomichub.io/market?collection_name=whatifreward'
  },
  {
    name: 'I Dare To Dream',
    collectionName: 'idaretodream',
    baseMP: '1 - 2,109,600 MP',
    role: 'Partner Attacker (Autism Awareness, HP Recoup)',
    marketUrl: 'https://wax.atomichub.io/market?collection_name=idaretodream'
  },
  {
    name: 'WaxStation 21',
    collectionName: 'waxstation21',
    baseMP: '350 - 10,000 MP',
    role: 'Mining Gear & Extractors',
    marketUrl: 'https://wax.atomichub.io/market?collection_name=waxstation21'
  },
  {
    name: 'Syndicate 21',
    collectionName: 'syndicates21',
    baseMP: '300 - 100,000 MP',
    role: 'Crime Syndicate Mobsters & Turf',
    marketUrl: 'https://wax.atomichub.io/market?collection_name=syndicates21'
  },
  {
    name: 'Clean Up Center',
    collectionName: 'cleanupcentr',
    baseMP: '300 MP',
    role: 'Eco Partner Miners',
    marketUrl: 'https://wax.atomichub.io/market?collection_name=cleanupcentr'
  },
  {
    name: 'Pixel Journey',
    collectionName: 'pixeljourney',
    baseMP: '300 MP',
    role: 'Partner Art & Explorers',
    marketUrl: 'https://wax.atomichub.io/market?collection_name=pixeljourney'
  },
  {
    name: 'Pantheon',
    collectionName: 'pantheon',
    baseMP: '300 MP',
    role: 'Mythic Partner Warriors',
    marketUrl: 'https://wax.atomichub.io/market?collection_name=pantheon'
  },
  {
    name: 'Planet Defense',
    collectionName: 'planetdefnft',
    baseMP: '300 MP',
    role: 'Sci-Fi Defenders',
    marketUrl: 'https://wax.atomichub.io/market?collection_name=planetdefnft'
  },
  {
    name: 'Facings',
    collectionName: 'facings',
    baseMP: '250 MP',
    role: 'Partner Avatars & Relics',
    marketUrl: 'https://wax.atomichub.io/market?collection_name=facings'
  },
  {
    name: 'Taco',
    collectionName: 'taco',
    baseMP: '100 MP',
    role: 'WAX Ecosystem Partner',
    marketUrl: 'https://wax.atomichub.io/market?collection_name=taco'
  },
  {
    name: 'MLB Topps',
    collectionName: 'mlb.topps',
    baseMP: '50 MP',
    role: 'Sports Collectibles',
    marketUrl: 'https://wax.atomichub.io/market?collection_name=mlb.topps'
  },
  {
    name: 'Futures Relic',
    collectionName: 'futuresrelic',
    baseMP: '15 MP',
    role: 'Partner Relics',
    marketUrl: 'https://wax.atomichub.io/market?collection_name=futuresrelic'
  },
  {
    name: 'Alien Worlds',
    collectionName: 'alien.worlds',
    baseMP: '10 MP',
    role: 'Mining Tools & Avatars',
    marketUrl: 'https://wax.atomichub.io/market?collection_name=alien.worlds'
  },
  {
    name: 'Pigmentia',
    collectionName: 'pigmentiawax',
    baseMP: '300 MP',
    role: 'Partner Art Collection',
    marketUrl: 'https://wax.atomichub.io/market?collection_name=pigmentiawax'
  },
  {
    name: 'We Dare 2 Dream',
    collectionName: 'wedare2dream',
    baseMP: '1 - 2,109,600 MP',
    role: 'Partner Attacker (Autism Awareness, HP Recoup)',
    marketUrl: 'https://wax.atomichub.io/market?collection_name=wedare2dream'
  }
];

function NFTStakingPage({
  session,
  playerData,
  nfts = [],
  stakedAttackers = [],
  totalMP = 0,
  totalBoost = 0,
  currentStamina = 0,
  refreshNfts
}) {
  const [feedback, setFeedback] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [selectedToStake, setSelectedToStake] = useState([]);
  const [showCollectionGuide, setShowCollectionGuide] = useState(false);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(timer);
  }, []);

  const handleRefresh = async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    setFeedback('Refreshing your NFT inventory, please wait...');
    try {
      if (refreshNfts) {
        await refreshNfts();
        setFeedback('Inventory refreshed successfully!');
      } else {
        setFeedback('Refresh function not available.');
      }
    } catch (e) {
      setFeedback(`Error refreshing inventory: ${e.message}`);
    }
    setIsRefreshing(false);
  };

  // Uses full supported list from gameConfig while filtering out equip-only special emblems
  const userStakeableNfts = useMemo(() => {
    const validCollections = Array.isArray(SUPPORTED_COLLECTIONS) && SUPPORTED_COLLECTIONS.length > 0
      ? SUPPORTED_COLLECTIONS
      : ['whatifreward', 'waxstation21', 'syndicates21', 'idaretodream'];

    return nfts.filter(nft => {
      const colName = nft.collection?.collection_name;
      if (!validCollections.includes(colName)) return false;
      const templateId = nft.template?.template_id ? Number(nft.template.template_id) : null;
      return !EMBLEM_TEMPLATES.has(templateId) && templateId !== 909865;
    });
  }, [nfts, playerData]);

  const userEmblemNfts = useMemo(() => {
    return nfts.filter(nft => {
      const templateId = nft.template?.template_id ? Number(nft.template.template_id) : null;
      return EMBLEM_TEMPLATES.has(templateId);
    });
  }, [nfts]);

  const toggleSelection = (nft) => {
    setSelectedToStake(prev => {
      if (prev.find(n => n.asset_id === nft.asset_id)) {
        return prev.filter(n => n.asset_id !== nft.asset_id);
      } else {
        if (prev.length >= 15) {
          setFeedback("You can only stake up to 15 NFTs at a time to prevent CPU limits.");
          return prev;
        }
        return [...prev, nft];
      }
    });
  };

  const handleBulkStake = async () => {
    if (!session || playerData?.isBlacklisted || selectedToStake.length === 0) {
      setFeedback("Cannot stake at this time.");
      return;
    }

    // 🛡️ DOUBLE CHECK: Prevent the active Gang Pass from bypassing the UI
    if (selectedToStake.some(nft => String(nft.asset_id) === String(playerData?.gangPassAssetId))) {
      setFeedback("Error: You cannot stake your active Gang Pass. Leave your gang to unbind it first.");
      return;
    }

    if (!window.confirm("⚠️ Warning: Staking will lock your NFTs in the WHATIF Dungeon for exactly 24 hours. You will not be able to unstake them early. Proceed?")) return;

    // ... rest of your function remains the same

    setIsLoading(true);
    setFeedback("Please approve the transaction in your wallet...");
    try {
      const actions = [{
        account: 'atomicassets',
        name: 'transfer',
        authorization: [session.permissionLevel],
        data: {
          from: session.actor.toString(),
          to: 'whatifdragon',
          asset_ids: selectedToStake.map(n => n.asset_id),
          memo: 'stake',
        },
      }];

      await session.transact({ actions });
      setFeedback("Stake transaction sent successfully! Please refresh in a few moments.");
      setSelectedToStake([]);
      if (refreshNfts) setTimeout(() => refreshNfts(), 3500);
    } catch (error) {
      setFeedback(`Error: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUnstake = async (stakeAssetId) => {
    if (!session || playerData?.isBlacklisted) return;
    if (!window.confirm(`Are you sure you want to unstake this item? It will be returned to your wallet.`)) return;

    setIsLoading(true);
    setFeedback(`Returning asset ${stakeAssetId} to your wallet...`);
    try {
      const actions = [{
        account: 'whatifdragon',
        name: 'unstake',
        authorization: [session.permissionLevel],
        data: {
          staker: session.actor.toString(),
          asset_ids: [stakeAssetId],
        },
      }];

      await session.transact({ actions });
      setFeedback("Unstake transaction sent successfully! Please refresh in a few moments.");
      if (refreshNfts) setTimeout(() => refreshNfts(), 3500);
    } catch (error) {
      setFeedback(`Error: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const getLockInfo = (stakedAtStr) => {
    if (!stakedAtStr) return { isLocked: false, label: '' };
    const stakedTime = new Date(stakedAtStr.endsWith("Z") ? stakedAtStr : stakedAtStr + "Z").getTime();
    const unlockTime = stakedTime + (24 * 60 * 60 * 1000);
    if (now < unlockTime) {
      const remaining = unlockTime - now;
      const hours = Math.floor(remaining / (60 * 60 * 1000));
      const minutes = Math.floor((remaining % (60 * 60 * 1000)) / (60 * 1000));
      return { isLocked: true, label: `Locked: ${hours}h ${minutes}m` };
    }
    return { isLocked: false, label: 'Unlocked' };
  };

  return (
    <div style={{ backgroundColor: 'rgba(0,0,0,0.75)', padding: '25px', borderRadius: '12px', maxWidth: '1000px', margin: 'auto', border: '1px solid #333', color: '#e0e0e0' }}>

      {/* Top Header & Refresh Bar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
        <div>
          <h2 style={{ color: '#ff9800', margin: '0 0 5px 0' }}>Dungeon Armoury (Staking)</h2>
          <p style={{ color: '#aaa', margin: 0, fontSize: '14px' }}>Stake eligible partner NFTs to generate Base Mining Power (MP) on-chain.</p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            onClick={() => setShowCollectionGuide(prev => !prev)}
            className="modern-button"
            style={{ margin: 0, padding: '10px 15px', background: showCollectionGuide ? '#00838f' : 'rgba(0, 229, 255, 0.15)', border: '1px solid #00e5ff', color: '#00e5ff' }}
          >
            {showCollectionGuide ? '✕ Close Guide' : '🛒 AtomicHub Links & MP Guide'}
          </button>
          <button
            onClick={handleRefresh}
            disabled={isRefreshing || isLoading}
            className="modern-button"
            style={{ margin: 0, padding: '10px 15px' }}
          >
            {isRefreshing ? 'Refreshing...' : 'Refresh Inventory'}
          </button>
        </div>
      </div>

      {/* Badge Claims */}
      <BadgeClaim session={session} nfts={nfts} refreshNfts={refreshNfts} />

      {/* Overview Stat Counters */}
      <div style={{ display: 'flex', gap: '20px', margin: '20px 0', background: 'rgba(0,0,0,0.5)', padding: '15px', borderRadius: '8px', border: '1px solid #444' }}>
        <div style={{ flex: 1, textAlign: 'center' }}>
          <h3 style={{ color: '#00e5ff', margin: '0 0 5px 0', fontSize: '14px', textTransform: 'uppercase' }}>Total Base MP</h3>
          <p style={{ fontSize: '26px', fontWeight: 'bold', margin: 0, color: '#fff' }}>{totalMP.toLocaleString()}</p>
          {nfts.some(nft => String(nft.template?.template_id || nft.template_id || '') === '909865') && (
            <span style={{ fontSize: '12px', color: '#00e5ff', display: 'block', marginTop: '4px', fontWeight: 'bold' }}>
              ⚡ Includes +5,000 MP Keycard
            </span>
          )}
        </div>
        <div style={{ width: '1px', background: '#444' }}></div>
        <div style={{ flex: 1, textAlign: 'center' }}>
          <h3 style={{ color: '#4caf50', margin: '0 0 5px 0', fontSize: '14px', textTransform: 'uppercase' }}>Total Boost</h3>
          <p style={{ fontSize: '26px', fontWeight: 'bold', margin: 0, color: '#4caf50' }}>+{totalBoost}%</p>
        </div>
        <div style={{ width: '1px', background: '#444' }}></div>
        <div style={{ flex: 1, textAlign: 'center' }}>
          <h3 style={{ color: '#ff9800', margin: '0 0 5px 0', fontSize: '14px', textTransform: 'uppercase' }}>Max Stamina</h3>
          <p style={{ fontSize: '26px', fontWeight: 'bold', margin: 0, color: '#ff9800' }}>{currentStamina}</p>
        </div>
      </div>

      {/* Supported Collections & AtomicHub Links Drawer */}
      {showCollectionGuide && (
        <div style={{ marginBottom: '25px', padding: '20px', background: 'rgba(0, 229, 255, 0.05)', border: '1px solid #00e5ff', borderRadius: '8px', animation: 'fadeIn 0.3s ease' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
            <h3 style={{ color: '#00e5ff', margin: 0 }}>🏛️ Supported Partner Collections on AtomicHub</h3>
            <span style={{ fontSize: '12px', color: '#aaa' }}>12 Collections Integrated</span>
          </div>
          <p style={{ fontSize: '13px', color: '#ccc', margin: '0 0 15px 0' }}>
            Acquire NFTs from any of these verified collections on AtomicHub to expand your Dungeon Mining Power (MP).
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '12px' }}>
            {COLLECTION_DETAILS.map(col => (
              <div
                key={col.collectionName}
                style={{
                  background: 'rgba(0, 0, 0, 0.6)',
                  border: '1px solid #444',
                  borderRadius: '6px',
                  padding: '12px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between'
                }}
              >
                <div>
                  <h4 style={{ margin: '0 0 4px 0', color: '#fff', fontSize: '15px' }}>{col.name}</h4>
                  <div style={{ fontSize: '11px', color: '#00e5ff', fontFamily: 'monospace', marginBottom: '6px' }}>
                    {col.collectionName}
                  </div>
                  <div style={{ fontSize: '12px', color: '#4caf50', fontWeight: 'bold', marginBottom: '4px' }}>
                    ⚡ {col.baseMP}
                  </div>
                  <p style={{ fontSize: '11px', color: '#aaa', margin: '0 0 10px 0', lineHeight: '1.3' }}>
                    {col.role}
                  </p>
                </div>
                <a
                  href={col.marketUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'block',
                    textAlign: 'center',
                    padding: '6px 10px',
                    background: 'linear-gradient(145deg, #007bff, #0056b3)',
                    color: '#fff',
                    textDecoration: 'none',
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    boxShadow: '0 2px 5px rgba(0,0,0,0.5)'
                  }}
                >
                  Shop on AtomicHub ↗
                </a>
              </div>
            ))}
          </div>
        </div>
      )}

      {feedback && (
        <p style={{ color: feedback.startsWith('Error') || feedback.startsWith('Failed') ? '#ff4b2b' : 'lightgreen', backgroundColor: '#222', padding: '12px', borderRadius: '6px', margin: '15px 0', border: '1px solid #444' }}>
          {feedback}
        </p>
      )}

      {playerData?.isBlacklisted && (
        <p style={{ color: 'red', fontWeight: 'bold', textAlign: 'center' }}>
          Your account is currently blacklisted and cannot perform staking actions.
        </p>
      )}

      {/* Main Dual-Column Interface */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginTop: '10px' }}>

        {/* Left Column: Wallet Stakeable NFTs */}
        <div>
          {nfts.some(nft => String(nft.template?.template_id) === '909865') && (
            <div style={{
              margin: '0 0 12px 0',
              padding: '10px 14px',
              background: 'linear-gradient(135deg, rgba(0, 229, 255, 0.12) 0%, rgba(124, 58, 237, 0.18) 100%)',
              border: '1px solid #00e5ff',
              borderRadius: '6px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <div style={{ fontSize: '13px' }}>
                <span style={{ marginRight: '6px' }}>🔐</span>
                <strong style={{ color: '#00e5ff' }}>Master Keycard Active:</strong>
                <span style={{ color: '#ccc', marginLeft: '4px' }}>+5,000 Base MP credited directly from your wallet!</span>
              </div>
              <span style={{ background: '#00e5ff', color: '#000', fontWeight: 'bold', padding: '2px 7px', borderRadius: '4px', fontSize: '11px' }}>
                +5,000 MP
              </span>
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <h3 style={{ margin: 0, fontSize: '16px' }}>Your Wallet NFTs ({userStakeableNfts.length})</h3>
            {selectedToStake.length > 0 && (
              <button
                onClick={handleBulkStake}
                disabled={isLoading || playerData?.isBlacklisted}
                className="modern-button"
                style={{ margin: 0, padding: '6px 12px', fontSize: '13px', background: 'linear-gradient(145deg, #28a745, #218838)' }}
              >
                Stake Selected ({selectedToStake.length}/15)
              </button>
            )}
          </div>

          <div style={{ minHeight: '350px', maxHeight: '450px', overflowY: 'auto', padding: '10px', border: '1px solid #444', borderRadius: '6px', background: 'rgba(0,0,0,0.3)' }}>
            {userStakeableNfts.length > 0 ? (
              userStakeableNfts.map(nft => {
                const stats = getAssetEcosystemStats(nft);
                const isSelected = !!selectedToStake.find(n => n.asset_id === nft.asset_id);
                return (
                  <div
                    key={nft.asset_id}
                    onClick={() => toggleSelection(nft)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      marginBottom: '8px',
                      padding: '8px',
                      background: isSelected ? 'rgba(0, 229, 255, 0.15)' : 'rgba(255,255,255,0.03)',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      border: isSelected ? '1px solid #00e5ff' : '1px solid #333',
                      transition: 'background 0.2s'
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      readOnly
                      style={{ transform: 'scale(1.2)', marginLeft: '5px', cursor: 'pointer' }}
                    />
                    <img
                      src={nft.data?.img ? resolveIpfsUrl(nft.data.img) : placeholderImage}
                      alt={nft.name}
                      style={{ width: '48px', height: '48px', objectFit: 'contain', borderRadius: '4px', background: '#111' }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ margin: 0, fontWeight: isSelected ? 'bold' : 'normal', fontSize: '13px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {nft.name}
                      </p>
                      <div style={{ display: 'flex', gap: '8px', fontSize: '11px', marginTop: '2px' }}>
                        <span style={{ color: '#888' }}>{nft.collection?.collection_name}</span>
                        {stats && stats.MP > 0 && <span style={{ color: '#00e5ff', fontWeight: 'bold' }}>⛏️ {stats.MP} MP</span>}
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div style={{ textAlign: 'center', padding: '40px 10px', color: '#888' }}>
                <p>No eligible ecosystem NFTs found in your wallet.</p>
                <button
                  onClick={() => setShowCollectionGuide(true)}
                  style={{ background: 'none', border: 'none', color: '#00e5ff', textDecoration: 'underline', cursor: 'pointer', fontSize: '13px' }}
                >
                  View supported collections to buy on AtomicHub
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Right Column: Actively Staked NFTs */}
        <div>
          <h3 style={{ margin: '0 0 10px 0', fontSize: '16px' }}>
            Actively Staked in Dungeon ({stakedAttackers?.length || 0})
          </h3>

          <div style={{ minHeight: '350px', maxHeight: '450px', overflowY: 'auto', padding: '10px', border: '1px solid #444', borderRadius: '6px', background: 'rgba(0,0,0,0.3)' }}>
            {stakedAttackers?.length > 0 ? (
              stakedAttackers.map(stake => {
                const { isLocked, label } = getLockInfo(stake.staked_at);
                const t = itemTemplates[stake.template_id];
                const rawImg = t?.img;
                const imgSrc = rawImg ? resolveIpfsUrl(rawImg) : placeholderImage;

                return (
                  <div
                    key={stake.asset_id || Math.random()}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      marginBottom: '8px',
                      padding: '8px',
                      background: 'rgba(255,255,255,0.03)',
                      borderRadius: '6px',
                      border: isLocked ? '1px solid #555' : '1px solid rgba(76, 175, 80, 0.3)'
                    }}
                  >
                    <div style={{ position: 'relative' }}>
                      <img
                        src={imgSrc}
                        alt={`Staked Asset ${stake.asset_id}`}
                        style={{ width: '48px', height: '48px', borderRadius: '4px', objectFit: 'cover', background: '#111' }}
                      />
                      {isLocked && (
                        <div style={{ position: 'absolute', top: '-6px', right: '-6px', fontSize: '14px', textShadow: '0 0 4px black' }}>
                          🔒
                        </div>
                      )}
                    </div>

                    <div style={{ flex: 1 }}>
                      <p style={{ margin: 0, fontSize: '13px', fontWeight: 'bold' }}>
                        Asset #{String(stake.asset_id).slice(-6)}
                      </p>
                      <p style={{ margin: '2px 0 0 0', color: '#00e5ff', fontSize: '12px', fontWeight: 'bold' }}>
                        MP: {stake.mp_provided ?? stake.mp ?? stake.staked_amount ?? 'Unknown'}
                      </p>
                      {isLocked && <p style={{ margin: '2px 0 0 0', color: '#ffcc00', fontSize: '11px' }}>{label}</p>}
                    </div>

                    <button
                      onClick={() => handleUnstake(stake.asset_id)}
                      disabled={isLoading || playerData?.isBlacklisted || isLocked}
                      className="modern-button"
                      style={{
                        marginLeft: 'auto',
                        padding: '6px 12px',
                        fontSize: '12px',
                        background: isLocked ? '#444' : 'linear-gradient(145deg, #c43a3a, #a33333)',
                        cursor: isLocked ? 'not-allowed' : 'pointer',
                        color: isLocked ? '#888' : '#fff'
                      }}
                    >
                      {isLocked ? 'Locked' : 'Unstake'}
                    </button>
                  </div>
                );
              })
            ) : (
              <p style={{ textAlign: 'center', padding: '40px 10px', color: '#888' }}>
                You have no NFTs currently staked to whatifdragon.
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Special Equip-Only Emblems Section */}
      {userEmblemNfts.length > 0 && (
        <div style={{ marginTop: '25px', background: 'rgba(255,215,0,0.05)', border: '1px solid #a07800', borderRadius: '8px', padding: '15px' }}>
          <h3 style={{ color: '#ffd700', margin: '0 0 6px 0', fontSize: '16px' }}>⚜️ Special Emblems (Equip-Only)</h3>
          <p style={{ margin: '0 0 12px 0', color: '#bbb', fontSize: '12px' }}>
            These NFTs cannot be staked for raw MP. Transfer to <code>whatifdragon</code> with memo <strong>equip:emblem</strong> to equip.
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
            {userEmblemNfts.map(nft => (
              <div key={nft.asset_id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px', background: 'rgba(255,215,0,0.08)', border: '1px solid #a07800', borderRadius: '6px' }}>
                <img
                  src={nft.data?.img ? resolveIpfsUrl(nft.data.img) : placeholderImage}
                  alt={nft.name}
                  style={{ width: '40px', height: '40px', borderRadius: '4px', objectFit: 'contain', background: '#111' }}
                />
                <div>
                  <p style={{ margin: 0, fontWeight: 'bold', color: '#ffd700', fontSize: '13px' }}>{nft.name}</p>
                  <p style={{ margin: 0, color: '#888', fontSize: '11px' }}>ID: {nft.asset_id}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

    </div>
  );
}

export default NFTStakingPage;